<template>
  <RevCard class="pt-24 px-24 pb-16 relative">
    <RevTag
      class="absolute -top-10 right-24"
      :label="
        i18n(translations.discountTag, { price: i18n.price(offer.subsidy) })
      "
      variant="success"
    />
    <div class="space-y-16">
      <div>
        <div>
          <div class="w-full flex justify-between items-center gap-8">
            <h3 class="heading-3">
              {{ offer.label }}
            </h3>

            <p
              v-if="keepPhoneNumber && keepPhoneNumber.prices.length === 1"
              class="body-2"
            >
              {{
                i18n(translations.onePeriodPrice, {
                  price: i18n.price(keepPhoneNumber.prices[0].price),
                })
              }}
            </p>

            <RevAccordionItem v-else-if="keepPhoneNumber" collapsed="expanded">
              <template #title>
                {{ i18n(translations.priceWhileKeepingTheSamePhoneNumber) }}
              </template>

              <template #body>
                <div>
                  <span class="text-static-default-low caption-striked">
                    {{
                      i18n(translations.onePeriodPrice, {
                        price: i18n.price(keepPhoneNumber.prices[1].price),
                      })
                    }}
                  </span>

                  <span class="body-1 ml-2">
                    {{
                      i18n(translations.onePeriodPrice, {
                        price: i18n.price(keepPhoneNumber.prices[0].price),
                      })
                    }}
                  </span>

                  <div
                    v-if="keepPhoneNumber.prices[0].monthsDuration"
                    class="text-static-default-low caption"
                  >
                    {{
                      i18n(translations.secondPeriodPrice, {
                        duration: getFormattedDuration(
                          keepPhoneNumber.prices[0].monthsDuration,
                        ),
                        secondPeriodPrice: i18n.price(
                          keepPhoneNumber.prices[1].price,
                        ),
                      })
                    }}
                  </div>

                  <span class="text-static-default-low caption">
                    <EnrollmentPeriod
                      :enrollment-period="offer.enrollmentPeriod"
                      :enrollment-translation="translations.enrollmentPeriod"
                    />
                  </span>
                </div>
              </template>
            </RevAccordionItem>
          </div>
        </div>

        <span class="text-static-default-low caption">
          <EnrollmentPeriod
            :enrollment-period="offer.enrollmentPeriod"
            :enrollment-translation="translations.enrollmentPeriod"
          />
        </span>

        <!-- New phone number price option -->
        <div
          v-if="
            newPhoneNumber &&
            keepPhoneNumber?.prices[0].price.amount !==
              newPhoneNumber.prices[0].price.amount
          "
        >
          <RevDivider />

          <RevAccordionItem class="pt-16">
            <template #title>
              {{ i18n(translations.priceWhileChangingPhoneNumber) }}
            </template>

            <template #body>
              <div class="flex flex-col">
                <span class="body-1">
                  {{
                    i18n(translations.onePeriodPrice, {
                      price: i18n.price(newPhoneNumber.prices[0].price),
                    })
                  }}
                </span>

                <span class="text-static-default-low caption">
                  <EnrollmentPeriod
                    :enrollment-period="offer.enrollmentPeriod"
                    :enrollment-translation="translations.enrollmentPeriod"
                  />
                </span>
              </div>
            </template>
          </RevAccordionItem>
        </div>
      </div>

      <div v-if="offer.baseOffer">
        <MobilePlanFlashOffer :base-offer="offer.baseOffer" :offer>
          <span
            v-if="offer.endDate"
            class="mt-8 caption text-static-default-mid"
          >
            {{
              i18n(translations.validUntil, {
                date: new Date(offer.endDate),
              })
            }}
          </span>
        </MobilePlanFlashOffer>
      </div>

      <BenefitsList v-if="withBenefits" :benefits="offer.benefits" />

      <RevLink
        class="caption-link flex items-center"
        @click="withBenefits = !withBenefits"
      >
        <div>
          <template v-if="!withBenefits">
            {{ i18n(translations.fullPlanDetails) }}
          </template>
          <template v-else>{{ i18n(translations.seeLess) }}</template>
        </div>

        <IconChevronUp v-if="withBenefits" />
        <IconChevronDown v-if="!withBenefits" />
      </RevLink>
    </div>
  </RevCard>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import type { MobilePlanOffer } from '@backmarket/http-api/src/api-specs-partnerships/mobile-plan/types/mobile-plan-offers'
import EnrollmentPeriod from '@backmarket/nuxt-layer-checkout/components/EnrollmentPeriod/EnrollmentPeriod.vue'
import { useFormatMonthsYearsDuration } from '@backmarket/nuxt-layer-checkout/composables/useFormatMonthsYearsDuration'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevAccordionItem } from '@ds/components/AccordionItem'
import { RevCard } from '@ds/components/Card'
import { RevDivider } from '@ds/components/Divider'
import { RevLink } from '@ds/components/Link'
import { RevTag } from '@ds/components/Tag'
import { IconChevronDown } from '@ds/icons/IconChevronDown'
import { IconChevronUp } from '@ds/icons/IconChevronUp'
import type { ReadonlyObjectDeep } from 'type-fest/source/readonly-deep'

import MobilePlanFlashOffer from '~/scopes/partnerships/components/MobilePlanFlashOffer/MobilePlanFlashOffer.vue'

import translations from './OfferCard.translations'
import BenefitsList from './components/BenefitsList/BenefitsList.vue'

const withBenefits = ref(false)

const props = defineProps<{
  offer: MobilePlanOffer | ReadonlyObjectDeep<MobilePlanOffer>
}>()

const i18n = useI18n()

const keepPhoneNumber = computed(() =>
  props.offer.priceOptions.find(
    (priceOption) => priceOption.option === 'KEEP_PHONE_NUMBER',
  ),
)

const newPhoneNumber = computed(() =>
  props.offer.priceOptions.find(
    (priceOption) => priceOption.option === 'NEW_PHONE_NUMBER',
  ),
)

const { getFormattedDuration } = useFormatMonthsYearsDuration()
</script>
